import React, { useState, useEffect, useRef } from 'react';
import { acquistiApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, Upload, Trash2, AlertTriangle, ChevronDown, ChevronUp, Landmark, ArrowLeftRight,
  TrendingUp, TrendingDown,
} from 'lucide-react';
import AcquistiHeader from '../../components/acquisti/AcquistiHeader';
import LinkGuida from '../../components/promemoria/LinkGuida';
import { fmtSoldi, fmtDataIt } from '../../lib/acquisti';

const Delta = ({ valore, pct, inline }) => {
  if (!valore) return null;
  const su = valore > 0;
  const Icona = su ? TrendingUp : TrendingDown;
  const colore = su ? 'text-red-600' : 'text-emerald-600';
  return (
    <span
      className={`${colore} ${inline ? 'ml-1.5 text-[10px]' : 'text-xs'} font-semibold inline-flex items-center gap-0.5 tabular-nums`}
    >
      <Icona className="w-3 h-3" />
      {su ? '+' : ''}{fmtSoldi(valore)}
      {pct !== null && pct !== undefined ? ` (${su ? '+' : ''}${pct}%)` : ''}
    </span>
  );
};

const AcquistiBancariPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const fileRef = useRef(null);
  const [dati, setDati] = useState(null);
  const [caricando, setCaricando] = useState(true);
  const [analisi, setAnalisi] = useState(null);
  const [inCorso, setInCorso] = useState(false);
  const [aperti, setAperti] = useState({});

  const carica = async () => {
    setCaricando(true);
    try {
      setDati(await acquistiApi.getBancari());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento dei costi bancari');
    } finally {
      setCaricando(false);
    }
  };

  useEffect(() => { carica(); }, []);

  const scegliFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setInCorso(true);
    try {
      setAnalisi(await acquistiApi.analizzaBancari(file));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'File non leggibile');
    } finally {
      setInCorso(false);
    }
  };

  const conferma = async () => {
    setInCorso(true);
    try {
      const res = await acquistiApi.confermaBancari(analisi.upload_id);
      toast.success(`${res.mesi_salvati} mesi salvati per il ${res.anno} · ${fmtSoldi(res.totale)}`);
      setAnalisi(null);
      carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setInCorso(false);
    }
  };

  const elimina = async (anno, mese, label) => {
    if (!window.confirm(`Eliminare i costi bancari di ${label}?`)) return;
    try {
      await acquistiApi.eliminaBancari(anno, mese);
      toast.success('Mese eliminato');
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eliminazione non riuscita');
    }
  };

  const toggle = (k) => setAperti((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="min-h-screen pb-24">
      <AcquistiHeader
        titolo="Costi bancari e di incasso"
        sottotitolo={dati ? `${dati.n_mesi} mesi · ${fmtSoldi(dati.totale)}` : 'Riepilogo mensile'}
        azione={isAdmin && (
          <Button
            size="sm"
            className="shrink-0 h-9 text-xs"
            onClick={() => fileRef.current?.click()}
            disabled={inCorso}
            data-testid="bancari-carica"
          >
            {inCorso ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Carica
          </Button>
        )}
      />
      <input
        ref={fileRef}
        type="file"
        accept=".xls,.xlsx,.xlsm"
        className="hidden"
        onChange={scegliFile}
        data-testid="bancari-file-input"
      />

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        <LinkGuida testo="Come si prepara l'Excel dal CSV della banca?" />
        {caricando && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        )}

        {dati?.confronto && (
          <Card data-testid="bancari-confronto">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
                {dati.confronto.anno} vs {dati.confronto.anno_precedente}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Stessi mesi a confronto ({dati.confronto.omogeneo.label})
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold tabular-nums" data-testid="bancari-confronto-totale">
                    {fmtSoldi(dati.confronto.omogeneo.totale)}
                  </p>
                  <Delta valore={dati.confronto.omogeneo.delta} pct={dati.confronto.omogeneo.delta_pct} />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  contro {fmtSoldi(dati.confronto.omogeneo.totale_precedente)} nel
                  {' '}{dati.confronto.anno_precedente}
                  {dati.confronto.omogeneo.parziale ? ' · ultimo mese ancora parziale' : ''}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t text-sm">
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground">
                    {dati.confronto.anno} in archivio
                  </p>
                  <p className="font-semibold tabular-nums">{fmtSoldi(dati.confronto.totale)}</p>
                  <p className="text-[11px] text-muted-foreground">{dati.confronto.n_mesi} mesi</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground">
                    {dati.confronto.anno_precedente} in archivio
                  </p>
                  <p className="font-semibold tabular-nums">{fmtSoldi(dati.confronto.totale_precedente)}</p>
                  <p className="text-[11px] text-muted-foreground">{dati.confronto.n_mesi_precedente} mesi</p>
                </div>
              </div>

              <div className="pt-2 border-t space-y-1.5">
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 items-baseline">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Voce</span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground text-right w-20">
                    {dati.confronto.anno}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground text-right w-20">
                    {dati.confronto.anno_precedente}
                  </span>
                </div>
                {dati.confronto.per_voce.map((v) => (
                  <div
                    key={v.voce}
                    className="grid grid-cols-[1fr_auto_auto] gap-x-3 items-baseline text-[11px]"
                    data-testid={`bancari-confronto-voce-${v.voce.replace(/[^\w]/g, '-')}`}
                  >
                    <span className="min-w-0">
                      {v.voce}
                      <Delta valore={v.delta} pct={v.delta_pct} inline />
                    </span>
                    <span className="tabular-nums text-right font-semibold">{fmtSoldi(v.totale)}</span>
                    <span className="tabular-nums text-right text-muted-foreground">
                      {fmtSoldi(v.totale_precedente)}
                    </span>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground pt-1">
                  Confronto calcolato sui soli mesi presenti in entrambi gli anni.
                </p>
              </div>

              <div className="pt-2 border-t space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Mese per mese</p>
                {dati.confronto.per_mese.map((m) => (
                  <div key={m.mese} className="grid grid-cols-[1fr_auto_auto] gap-x-3 items-baseline text-[11px]">
                    <span className="text-muted-foreground">
                      {m.label}{m.parziale ? ' (parziale)' : ''}
                    </span>
                    <span className="tabular-nums text-right w-20 font-semibold">
                      {m.totale === null ? '—' : fmtSoldi(m.totale)}
                    </span>
                    <span className="tabular-nums text-right w-20 text-muted-foreground">
                      {m.totale_precedente === null ? '—' : fmtSoldi(m.totale_precedente)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {dati?.doppioni?.length > 0 && (
          <Card className="border-amber-300" data-testid="bancari-doppioni">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-4 h-4" /> {dati.doppioni.length} possibili doppioni
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Queste voci sono fatture: nello stesso mese risulta anche una fattura elettronica
                dello stesso fornitore, quindi il costo potrebbe essere contato due volte.
              </p>
              {dati.doppioni.map((d) => (
                <div
                  key={`${d.anno}-${d.mese}-${d.voce}`}
                  className="rounded-lg border border-amber-200 bg-amber-50/60 p-3"
                  data-testid={`bancari-doppione-${d.anno}-${d.mese}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold">{d.voce}</span>
                    <span className="text-sm font-bold tabular-nums">{fmtSoldi(d.importo)}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{d.label}</p>
                  {d.fatture.map((f) => (
                    <p key={f.id} className="text-[11px] text-amber-800">
                      {f.riferimento} · {fmtSoldi(f.importo)} del {fmtDataIt(f.data)}
                    </p>
                  ))}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {dati && dati.anni.length === 0 && (
          <Card data-testid="bancari-vuoto">
            <CardContent className="p-4 space-y-2">
              <p className="text-sm font-semibold">Nessun costo bancario in archivio</p>
              <p className="text-xs text-muted-foreground">
                Carica il file Excel con il riepilogo mensile (primo foglio: voci in riga, mesi in
                colonna). Gli importi entrano nei costi totali e nel bilancio sotto la categoria
                BANCARI.
              </p>
              {isAdmin && (
                <Button size="sm" className="mt-1" onClick={() => fileRef.current?.click()} data-testid="bancari-carica-vuoto">
                  <Upload className="w-4 h-4 mr-1" /> Carica il riepilogo
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {dati?.anni.map((anno) => (
          <Card key={anno.anno} data-testid={`bancari-anno-${anno.anno}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-muted-foreground" /> {anno.anno}
                </span>
                <span className="tabular-nums" data-testid={`bancari-totale-${anno.anno}`}>
                  {fmtSoldi(anno.totale)}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                {anno.mesi.map((m) => {
                  const chiave = `${m.anno}-${m.mese}`;
                  return (
                    <div key={chiave} className="rounded-lg border p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => toggle(chiave)}
                          className="flex items-center gap-1.5 text-sm font-medium min-w-0"
                          data-testid={`bancari-mese-${chiave}`}
                        >
                          {aperti[chiave] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          <span className="truncate">{m.periodo_label}</span>
                          {m.parziale && (
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              al {m.aggiornato_al}
                            </Badge>
                          )}
                        </button>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm font-bold tabular-nums">{fmtSoldi(m.totale)}</span>
                          {isAdmin && (
                            <button
                              onClick={() => elimina(m.anno, m.mese, m.periodo_label)}
                              className="text-destructive"
                              data-testid={`bancari-elimina-${chiave}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {aperti[chiave] && (
                        <div className="mt-2 pt-2 border-t space-y-1">
                          {m.voci.map((v) => (
                            <div key={v.voce} className="flex items-baseline justify-between gap-2 text-[11px]">
                              <span className="text-muted-foreground min-w-0">{v.voce}</span>
                              <span className="tabular-nums shrink-0">{fmtSoldi(v.importo)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="pt-2 border-t space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Totale per voce nell'anno
                </p>
                {anno.per_voce.map((v) => (
                  <div key={v.voce} className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-muted-foreground min-w-0">{v.voce}</span>
                    <span className="font-semibold tabular-nums shrink-0">{fmtSoldi(v.totale)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {dati && (
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Competenze, commissioni, canoni e bolli sono importi addebitati sul conto: non hanno
            IVA da scorporare, quindi nei prospetti compaiono identici nella colonna lorda e in
            quella netta. Vengono sommati ai costi totali e al bilancio nella categoria BANCARI.
          </p>
        )}
      </main>

      <Dialog open={!!analisi} onOpenChange={(v) => !v && setAnalisi(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="bancari-anteprima">
          <DialogHeader>
            <DialogTitle>Anteprima costi bancari {analisi?.anteprima?.anno}</DialogTitle>
            <DialogDescription className="text-xs">
              Foglio letto: «{analisi?.anteprima?.foglio}». Gli altri fogli vengono ignorati.
            </DialogDescription>
          </DialogHeader>
          {analisi && (
            <div className="space-y-3">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Totale da caricare
                </p>
                <p className="text-3xl font-bold tabular-nums" data-testid="bancari-anteprima-totale">
                  {fmtSoldi(analisi.anteprima.totale)}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {analisi.anteprima.mesi.length} mesi · {analisi.anteprima.per_voce.length} voci
                </p>
              </div>

              {analisi.esistenti?.length > 0 && (
                <p className="text-[11px] text-amber-700" data-testid="bancari-anteprima-sostituzione">
                  Attenzione: {analisi.esistenti.length} di questi mesi sono già in archivio e
                  verranno sostituiti.
                </p>
              )}
              {analisi.anteprima.scarti?.length > 0 && (
                <p className="text-[11px] text-amber-700">
                  Scarto fra somma delle voci e riga TOTALE in: {analisi.anteprima.scarti.join(', ')}
                </p>
              )}

              <div className="space-y-1 pt-2 border-t">
                {analisi.anteprima.mesi.map((m) => (
                  <div key={m.mese} className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">
                      {m.label}
                      {m.parziale ? ` (al ${m.aggiornato_al})` : ''}
                    </span>
                    <span className="font-semibold tabular-nums">{fmtSoldi(m.totale)}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-1 pt-2 border-t">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Voci</p>
                {analisi.anteprima.per_voce.map((v) => (
                  <div key={v.voce} className="flex items-baseline justify-between gap-2 text-[11px]">
                    <span className="text-muted-foreground min-w-0">{v.voce}</span>
                    <span className="tabular-nums shrink-0">{fmtSoldi(v.importo)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAnalisi(null)} data-testid="bancari-annulla">
              Annulla
            </Button>
            <Button onClick={conferma} disabled={inCorso} data-testid="bancari-conferma">
              {inCorso ? 'Salvataggio…' : 'Conferma e salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcquistiBancariPage;
